import { useState } from 'react';
import Faq from '../components/Faq';
import { FaArrowLeft, FaPlay, FaCheckCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const steps = [
  {
    id: 1,
    title: 'Landing Page',
    description: 'Halaman pertama saat user awal buka aplikasi SmartSecure',
    image: '/guide1.png',
    details: [
      'Buka aplikasi SmartSecure untuk pertama kali',
      'Lihat informasi tentang layanan perlindungan data',
      'Klik tombol "Lindungi Data Saya" untuk melanjutkan',
      'Mulai perjalanan keamanan digital Anda',
    ],
  },
  {
    id: 2,
    title: 'Berlangganan Page',
    description: 'Masukan email untuk mendapatkan gratis 1 bulan pertama',
    image: '/guide2.png',
    details: [
      'Masukkan alamat email aktif Anda',
      'Pilih paket berlangganan yang sesuai kebutuhan',
      'Dapatkan penawaran khusus 1 bulan gratis',
      'Klik "Bayar 1 Bulan, Nikmati 2 Bulan Pertama"',
    ],
  },
  {
    id: 3,
    title: 'Konfirmasi Pembayaran',
    description: 'Konfirmasi detail berlangganan dan pilih metode pembayaran',
    image: '/guide3.png',
    details: [
      'Review detail paket berlangganan yang dipilih',
      'Pilih metode pembayaran (Transfer Bank, E-wallet, Kartu Kredit)',
      'Pastikan informasi pembayaran sudah benar',
      'Klik tombol "Bayar" untuk menyelesaikan transaksi',
    ],
  },
  {
    id: 4,
    title: 'Perlindungan Aktif',
    description: 'Status perlindungan aktif setelah pembayaran berhasil',
    image: '/guide4.png',
    details: [
      'Pembayaran berhasil diproses dan dikonfirmasi',
      'Status perlindungan otomatis berubah menjadi "Aktif"',
      'Sistem mulai memantau keamanan data Anda',
      'Klik "Buka Dashboard SmartSecure" untuk melanjutkan',
    ],
  },
  {
    id: 5,
    title: 'Tambahkan Perlindungan',
    description: 'Masukkan email dan nomor telepon untuk perlindungan maksimal',
    image: '/guide5.png',
    details: [
      'Tambahkan email dan nomor telepon yang ingin dipantau',
      'Aktifkan notifikasi real-time untuk ancaman keamanan',
      'Atur preferensi jenis perlindungan data pribadi',
      'Verifikasi kontak untuk menerima alert keamanan',
    ],
  },
  {
    id: 6,
    title: 'Data Terlindungi',
    description: 'Monitoring berkala laporan kebocoran data pribadi Anda',
    image: '/guide6.png',
    details: [
      'Dashboard menampilkan status keamanan real-time',
      'Laporan mingguan tentang ancaman yang berhasil diblokir',
      'Notifikasi instan jika terdeteksi kebocoran data',
      'Akses riwayat lengkap aktivitas keamanan Anda',
    ],
  },
];

export default function Guide() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  return (
    <main className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft className="mr-2" />
            <span>Kembali</span>
          </button>
          <h1 className="text-lg font-bold">Panduan Penggunaan</h1>
          <div className="w-16"></div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#3723B5] to-[#3723B5] rounded-2xl p-6 mb-6 text-white">
          <h2 className="text-2xl font-bold mb-2">
            Panduan Lengkap SmartSecure
          </h2>
          <p className="text-sm opacity-90 mb-4">
            Gratis 1 Bulan{' '}
            <span className="font-bold">+ Bonus Bulan Kedua</span>
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Progress</span>
            <span className="text-sm text-gray-600">
              {activeStep}/6 Langkah
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-[#3723B5] to-[#3723B5] h-2 rounded-full transition-all duration-500"
              style={{ width: `${(activeStep / 6) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Steps Carousel */}
        <div className="mb-8 relative">
          <div className="bg-white rounded-2xl p-0 shadow-sm border-2 border-[#3723B5] flex flex-col items-center overflow-hidden">
            <div className="flex items-center justify-between w-full px-6 pt-6 mb-4">
              <button
                onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
                disabled={activeStep === 1}
                className="p-2 rounded-full bg-gray-100 text-[#3723B5] disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Sebelumnya"
              >
                <svg
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>
              <h3 className="font-bold text-lg text-center flex-1">
                {steps[activeStep - 1].title}
              </h3>
              <button
                onClick={() =>
                  setActiveStep(Math.min(steps.length, activeStep + 1))
                }
                disabled={activeStep === steps.length}
                className="p-2 rounded-full bg-gray-100 text-[#3723B5] disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Selanjutnya"
              >
                <svg
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
                </svg>
              </button>
            </div>
            <p className="text-gray-600 mb-4 text-center px-6">
              {steps[activeStep - 1].description}
            </p>
            <div>
              <img
                src={steps[activeStep - 1].image}
                alt={steps[activeStep - 1].title}
                className="w-full object-contain"
                style={{ maxHeight: '600px' }}
              />
            </div>
            <div className="space-y-3 w-full px-6 pb-6">
              <h4 className="font-semibold text-[#3723B5]">Detail Langkah:</h4>
              <ul className="space-y-2">
                {steps[activeStep - 1].details.map((detail, index) => (
                  <li
                    key={index}
                    className="flex items-start space-x-3 text-sm"
                  >
                    <FaCheckCircle className="text-green-500 text-xs mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex space-x-3 mb-8">
          <button
            onClick={() => setActiveStep(Math.max(1, activeStep - 1))}
            disabled={activeStep === 1}
            className="flex-1 py-3 border-2 border-gray-300 text-gray-600 font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:border-gray-400 transition-colors"
          >
            Langkah Sebelumnya
          </button>
          <button
            onClick={() => setActiveStep(Math.min(6, activeStep + 1))}
            disabled={activeStep === 6}
            className="flex-1 py-3 bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
          >
            {activeStep === 6 ? 'Selesai' : 'Langkah Selanjutnya'}
          </button>
        </div>

        {/* FAQ Section */}
        <Faq />

        {/* CTA Button */}
        <div className="text-center">
          <button
            onClick={() => navigate('/subscribe')}
            className="w-full py-4 bg-gradient-to-r from-[#3723B5] to-[#3723B5] text-white font-bold rounded-xl text-lg shadow-lg hover:shadow-xl transition-all mb-4"
          >
            🚀 Mulai Berlangganan Sekarang
          </button>
          <p className="text-sm text-gray-600">
            Dapatkan 1 bulan gratis + bonus bulan kedua
          </p>
        </div>
      </div>
    </main>
  );
}
